import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Loader,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { IconAlertTriangle, IconLogin2, IconServer2 } from '@tabler/icons-react';
import { ROLES, type Role } from '@zebra/core';
import { CenteredCard } from '../components/CenteredCard';
import { useAuth } from '../auth/AuthContext';

/** How long the tenant-loading spinner runs before the copy admits the API
 * might be cold-starting, rather than just leaving the user staring at a bare
 * spinner (Aurora Serverless v2 auto-pauses after 10min idle; first request
 * after that can take 15-30s — see AuthContext.refreshMemberships's retry
 * comment for the full picture). */
const SLOW_LOAD_HINT_DELAY_MS = 4000;

/** Inline four-square Microsoft mark. Microsoft's "Sign in with Microsoft"
 * branding guidelines specify this exact glyph (not a generic lock/arrow
 * icon) in these four colors — drawn as plain rects here since this task
 * disallows adding new asset files. */
function MicrosoftLogo() {
  return (
    <svg width={18} height={18} viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: LoginPage,
});

const ROLE_OPTIONS = ROLES.map((role) => ({ value: role, label: role }));

// Exported (not just used via `Route`'s `component`) so login.test.tsx can render
// it directly against a mocked AuthProvider without pulling in TanStack Router's
// real route-matching machinery.
export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();

  const [devToken, setDevToken] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [tenantLabel, setTenantLabel] = useState('');
  const [role, setRole] = useState<Role>('OPERATIONS');
  const [error, setError] = useState<string | null>(null);

  // Whether an active tenant already existed the moment this page mounted. A
  // fresh sign-in lands here with none — that's the case that should skip the
  // chooser entirely once the (only) membership resolves. A deliberate "Manage
  // tenants…" visit (TenantSwitcher, on an already-authenticated session) has
  // one already, and must keep showing the chooser rather than bouncing straight
  // back out — captured once via ref so a later `switchTenant` doesn't retrigger it.
  const hadActiveTenantOnMount = useRef(Boolean(auth.activeTenant));

  // Cold-start hint: AuthContext.refreshMemberships already retries silently
  // under 'loading' (see its comment), so a slow /me can leave this screen
  // spinning for 15s+ with no explanation — which is exactly what read as "login
  // is broken" (Stuart, sat on it for 60s). After a few seconds of continuous
  // loading, add a second line owning the wait instead of hiding it. Timer is
  // re-armed/cleared whenever membershipsStatus changes so a fast load never
  // shows it and a stalled one always does, cleaned up on unmount either way.
  const [showSlowLoadHint, setShowSlowLoadHint] = useState(false);
  useEffect(() => {
    if (auth.membershipsStatus !== 'loading') {
      setShowSlowLoadHint(false);
      return;
    }
    const timer = setTimeout(() => setShowSlowLoadHint(true), SLOW_LOAD_HINT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [auth.membershipsStatus]);

  function goToApp() {
    void navigate({ to: redirect ?? '/' });
  }

  // A token means the browser arrived from /auth-callback (or is revisiting with
  // a persisted session) and needs the server's membership list to render the
  // chooser below — services/api has no client-side substitute (orchestrator
  // ruling, 2026-08-24). Keyed on auth.token, NOT run-once: on a fresh sign-in
  // the callback page's setToken commits AFTER this page has already mounted
  // (React batches the state update across the navigate()), so a mount-only
  // effect saw token=null, never fetched, and left the spinner stuck on 'idle'
  // forever (Stuart, prod, 2026-08-25 ~14:02 — the server log showed no request
  // at all during a minute-long spinner). The 'idle' guard plus AuthContext's
  // refreshInFlightRef keep this from double-firing when token is already
  // present at mount.
  useEffect(() => {
    if (auth.token && auth.membershipsStatus === 'idle') void auth.refreshMemberships();
  }, [auth.token, auth.membershipsStatus]);

  // Single-tenant fast path: once the server confirms exactly one membership
  // (and refreshMemberships has already auto-selected it — see AuthContext.tsx),
  // skip the chooser and go straight into the app, UNLESS this was a deliberate
  // "Manage tenants…" visit (see hadActiveTenantOnMount above).
  useEffect(() => {
    if (
      !hadActiveTenantOnMount.current &&
      auth.membershipsStatus === 'ready' &&
      auth.tenants.length === 1 &&
      auth.activeTenant
    ) {
      goToApp();
    }
  }, [auth.membershipsStatus, auth.tenants.length, auth.activeTenant]);

  function handleDevLogin() {
    if (!devToken.trim() || !tenantId.trim() || !tenantLabel.trim()) {
      setError('Dev JWT, tenant ID and a label are all required.');
      return;
    }
    setError(null);
    auth.loginDev({ token: devToken.trim(), tenantId: tenantId.trim(), label: tenantLabel.trim(), role });
    goToApp();
  }

  function handleAddTenant() {
    if (!tenantId.trim() || !tenantLabel.trim()) {
      setError('Tenant ID and a label are required.');
      return;
    }
    setError(null);
    auth.addTenant({ tenantId: tenantId.trim(), label: tenantLabel.trim(), role });
    goToApp();
  }

  function handleCognitoLogin() {
    try {
      auth.beginCognitoLogin();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not start Cognito sign-in.');
    }
  }

  // Already have a verified identity (token) — the chooser below is entirely
  // server-driven from here (GET /me via refreshMemberships). This also covers
  // "manage tenants" for an already-active session (TenantSwitcher's "Manage
  // tenants…" item routes here).
  if (auth.token) {
    if (auth.membershipsStatus === 'idle' || auth.membershipsStatus === 'loading') {
      return (
        // Deliberately NOT titled "Choose a tenant": single-membership users
        // auto-enter and never see a chooser, so during the load this titled
        // itself after a screen most users will never meet (Stuart read it as
        // a redundant extra step, 2026-08-25).
        <CenteredCard title="Signing you in" subtitle={auth.userEmail ?? undefined}>
          <Center py="lg">
            <Stack align="center" gap="xs">
              <Loader />
              <Text c="dimmed" size="sm">
                Loading your access…
              </Text>
              {showSlowLoadHint && (
                <Text c="dimmed" size="xs" ta="center">
                  Waking the system after a quiet spell — this can take up to a minute.
                </Text>
              )}
            </Stack>
          </Center>
        </CenteredCard>
      );
    }

    if (auth.membershipsStatus === 'error') {
      return (
        <CenteredCard title="Signing you in" subtitle={auth.userEmail ?? undefined}>
          <Stack gap="md">
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              Still starting up or offline. Try again in a few seconds.
            </Alert>
            <Button onClick={() => void auth.refreshMemberships()}>Retry</Button>
            <Anchor size="sm" ta="center" onClick={() => auth.logout()}>
              Sign out
            </Anchor>
          </Stack>
        </CenteredCard>
      );
    }

    // membershipsStatus === 'ready' from here on.
    if (auth.tenants.length === 0) {
      return (
        <CenteredCard title="Choose a tenant" subtitle={auth.userEmail ?? undefined}>
          <Stack gap="md">
            <Alert color="blue" icon={<IconAlertTriangle size={16} />}>
              Your sign-in worked, but this account has no tenant access yet. Ask an
              administrator to add you: Settings → Users &amp; roles → add member.
            </Alert>
            <Anchor size="sm" ta="center" onClick={() => auth.logout()}>
              Sign out
            </Anchor>
          </Stack>
        </CenteredCard>
      );
    }

    return (
      <CenteredCard title="Choose a tenant" subtitle={auth.userEmail ?? undefined}>
        <Stack gap="md">
          <Stack gap="xs">
            {auth.tenants.map((tenant) => (
              <Button
                key={tenant.tenantId}
                variant={tenant.tenantId === auth.activeTenant?.tenantId ? 'filled' : 'default'}
                fullWidth
                justify="space-between"
                rightSection={
                  <Badge variant="light" color="gray" size="sm">
                    {tenant.role}
                  </Badge>
                }
                onClick={() => {
                  auth.switchTenant(tenant.tenantId);
                  goToApp();
                }}
              >
                {tenant.label}
              </Button>
            ))}
          </Stack>
          {auth.devAuthEnabled && (
            <>
              <Text size="sm" c="dimmed">
                Add another tenant this account belongs to:
              </Text>
              <TenantFields
                tenantId={tenantId}
                setTenantId={setTenantId}
                tenantLabel={tenantLabel}
                setTenantLabel={setTenantLabel}
                role={role}
                setRole={setRole}
              />
              {error && (
                <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                  {error}
                </Alert>
              )}
              <Button onClick={handleAddTenant}>Add tenant &amp; continue</Button>
            </>
          )}
          <Anchor size="sm" ta="center" onClick={() => auth.logout()}>
            Sign out
          </Anchor>
        </Stack>
      </CenteredCard>
    );
  }

  // The deployed default (devAuthEnabled=false) has exactly one way to sign in,
  // so it renders the pane directly — a single-tab Tabs.List reads as a broken
  // orphan control (Stuart, 2026-08-25). The error alert lives in the pane:
  // beginCognitoLogin can throw before any navigation happens. No extra
  // "sign in with your Microsoft account" line above the button (removed
  // 2026-08-25, round 2): CenteredCard's "Sign in to continue" subtitle
  // already says that, and the button itself names Microsoft — a third
  // repetition read as padding, not help.
  const cognitoPane = (
    <Stack>
      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {error}
        </Alert>
      )}
      {/* variant="default" (not the app's graphite-filled primary) deliberately:
          Microsoft's "Sign in with Microsoft" branding guidelines call for a
          neutral light/dark button carrying their own four-square mark, not
          the host app's brand color — and Mantine's "default" variant already
          resolves to a scheme-aware near-white/near-black surface via
          `--mantine-color-default*`, so it needs no hardcoded colors to look
          right in both light and dark mode. */}
      <Button onClick={handleCognitoLogin} variant="default" fullWidth leftSection={<MicrosoftLogo />}>
        Sign in with Microsoft
      </Button>
    </Stack>
  );

  return (
    <CenteredCard title="Zebra Stock" subtitle="Sign in to continue">
      {auth.devAuthEnabled ? (
        <Tabs defaultValue="cognito">
          <Tabs.List>
            <Tabs.Tab value="cognito" leftSection={<IconLogin2 size={16} />}>
              Sign in
            </Tabs.Tab>
            <Tabs.Tab value="dev" leftSection={<IconServer2 size={16} />}>
              Developer
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="cognito" pt="md">
            {cognitoPane}
          </Tabs.Panel>

          <Tabs.Panel value="dev" pt="md">
            <Stack>
              <Text size="sm" c="dimmed">
                Local/testing only — paste a JWT self-signed with services/api&apos;s
                DEV_JWT_SECRET (AUTH_MODE=dev), plus the tenant to act as.
              </Text>
              <TextInput
                label="Dev JWT"
                placeholder="eyJhbGciOi…"
                value={devToken}
                onChange={(event) => setDevToken(event.currentTarget.value)}
              />
              <TenantFields
                tenantId={tenantId}
                setTenantId={setTenantId}
                tenantLabel={tenantLabel}
                setTenantLabel={setTenantLabel}
                role={role}
                setRole={setRole}
              />
              {error && (
                <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                  {error}
                </Alert>
              )}
              <Button onClick={handleDevLogin}>Sign in</Button>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      ) : (
        cognitoPane
      )}
    </CenteredCard>
  );
}

function TenantFields(props: {
  tenantId: string;
  setTenantId: (value: string) => void;
  tenantLabel: string;
  setTenantLabel: (value: string) => void;
  role: Role;
  setRole: (value: Role) => void;
}) {
  return (
    <>
      <TextInput
        label="Tenant ID"
        placeholder="e.g. sabie"
        value={props.tenantId}
        onChange={(event) => props.setTenantId(event.currentTarget.value)}
      />
      <TextInput
        label="Label"
        placeholder="e.g. Sabie Pellets"
        value={props.tenantLabel}
        onChange={(event) => props.setTenantLabel(event.currentTarget.value)}
      />
      <Select
        label="Role"
        data={ROLE_OPTIONS}
        value={props.role}
        onChange={(value) => value && props.setRole(value as Role)}
        allowDeselect={false}
      />
    </>
  );
}
